import React, { useState } from "react";
import { Fieldset, Legend } from "@/components/fieldset";
import { Badge } from "@/components/badge";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
} from "@heroicons/react/20/solid";
import type { IExamPaper, IQuestion } from "@/apis/exam_papers/types";
import CategorySelector from "@/pages/exam_papers/components/CategorySelector";
import CorrectChoiceSelector from "@/pages/exam_papers/components/CorrectChoiceSelector";
import { useNavigate } from "react-router-dom";
import { getPaperTypeLabel } from "@/constants/exam";
import { getDifficultyLabel, getSectionLabel } from "@/utils/exam";

interface ExamPaperDetailsProps {
  examPaper: IExamPaper;
  onPreviewQuestion?: (moduleNumber: number, questionNumber: number) => void;
}

const ExamPaperDetails: React.FC<ExamPaperDetailsProps> = ({
  examPaper,
  onPreviewQuestion,
}) => {
  const navigate = useNavigate();
  // State to track expanded sections
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  // Navigate to edit questions page
  const navigateToEditQuestions = () => {
    navigate(`/exam_papers/${examPaper.id}/edit_questions`);
  };

  // Toggle section expansion
  const toggleSection = (sectionIndex: number) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex],
    }));
  };

  // Format date utility function
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Get exam type name utility function
  const getExamTypeName = (type: string) => {
    switch (type) {
      case "SAT":
        return "SAT";
      case "ACT":
        return "ACT";
      case "TOEFL":
        return "TOEFL";
      default:
        return type;
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "active":
        return <Badge color="lime">Active</Badge>;
      case "inactive":
        return <Badge color="zinc">Inactive</Badge>;
      case "draft":
        return <Badge color="yellow">Draft</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getAccessDisplay = (isPrivate?: boolean) => {
    if (isPrivate === true) {
      return <Badge color="zinc">Private</Badge>;
    }
    return <Badge color="lime">Public</Badge>;
  };

  // Calculate total duration from sections
  const getTotalDuration = () => {
    return (
      examPaper.sections?.reduce(
        (total, section) => total + (section.duration || 0),
        0
      ) || 0
    );
  };

  // Calculate total questions from sections
  const getTotalQuestions = () => {
    return (
      examPaper.sections?.reduce(
        (total, section) => total + (section.question_ids?.length || 0),
        0
      ) || 0
    );
  };

  // Define a detail item component for consistent styling
  const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({
    label,
    value,
  }) => (
    <div className="grid grid-cols-3 gap-4 py-3">
      <dt className="font-medium text-gray-900">{label}</dt>
      <dd className="col-span-2 text-gray-700">{value}</dd>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Basic Information */}
      <Fieldset>
        <Legend>Basic Information</Legend>
        <div className="mt-4 border-t border-b border-gray-100">
          <dl className="divide-y divide-gray-100">
            <DetailItem label="Title" value={examPaper.title} />
            <DetailItem
              label="Exam Type"
              value={getExamTypeName(examPaper.exam_type)}
            />
            <DetailItem
              label="Subject"
              value={getPaperTypeLabel(examPaper.subject)}
            />
            <DetailItem
              label="Difficulty"
              value={getDifficultyLabel(examPaper.difficulty)}
            />
            <DetailItem
              label="Duration"
              value={`${getTotalDuration()} minutes`}
            />
            <DetailItem
              label="Status"
              value={getStatusDisplay(examPaper.status)}
            />
            <DetailItem
              label="Student Access"
              value={getAccessDisplay(examPaper.is_private)}
            />
            {examPaper.choice_count && (
              <DetailItem
                label="객관식 유형"
                value={`${examPaper.choice_count}지선다`}
              />
            )}
            <DetailItem label="Total Questions" value={getTotalQuestions()} />
            <DetailItem
              label="Created"
              value={formatDate(examPaper.created_at)}
            />
          </dl>
        </div>
      </Fieldset>

      {/* Sections Information */}
      <Fieldset>
        <div className="flex justify-between items-center mb-4">
          <Legend>Sections & Questions</Legend>
          <Button
            onClick={navigateToEditQuestions}
            className="flex items-center gap-1 text-sm"
            outline
          >
            <PencilIcon className="h-4 w-4" />
            <span>Edit Questions</span>
          </Button>
        </div>
        <div className="space-y-4 mt-4">
          {examPaper.sections && examPaper.sections.length > 0 ? (
            examPaper.sections.map((section, sectionIndex) => (
              <div
                key={sectionIndex}
                className="border border-gray-200 rounded-md overflow-hidden"
              >
                {/* Section Header */}
                <div
                  className="px-4 py-3 bg-gray-50 flex justify-between items-center cursor-pointer"
                  onClick={() => toggleSection(sectionIndex)}
                >
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {getSectionLabel(examPaper.exam_type)} {sectionIndex + 1}
                    </h3>
                    <div className="mt-1 text-sm text-gray-500">
                      <span>{section.duration} minutes</span>
                      <span className="mx-2">•</span>
                      <span>{section.question_ids?.length || 0} questions</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      color="white"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        navigate(
                          `/exam_papers/${examPaper.id}/preview/${
                            sectionIndex + 1
                          }`
                        );
                      }}
                    >
                      Preview
                    </Button>
                    <Button plain>
                      {expandedSections[sectionIndex] ? (
                        <ChevronUpIcon className="h-5 w-5" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Questions List (visible when expanded) */}
                {expandedSections[sectionIndex] && (
                  <div className="p-4 bg-white">
                    {!section.questions || section.questions.length === 0 ? (
                      <Text className="text-center text-gray-500">
                        No questions in this section.
                      </Text>
                    ) : (
                      <div className="space-y-4">
                        {section.questions.map((question, qIndex) => (
                          <div
                            key={question.id}
                            className="p-3 border border-gray-200 rounded-md"
                          >
                            {/* Question header */}
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center">
                                <span className="font-medium mr-2">
                                  Question #{qIndex + 1}
                                </span>
                              </div>

                              {/* Category Selector */}
                              <div className="flex items-center">
                                <div className="">
                                  <CategorySelector
                                    question={question}
                                    examType={examPaper?.exam_type}
                                    subject={examPaper?.subject}
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="space-y-2">
                              {/* Question Text */}
                              <div>
                                <div
                                  className="mt-1 quill-content"
                                  dangerouslySetInnerHTML={{
                                    __html: question.question,
                                  }}
                                />
                              </div>

                              {/* Choices */}
                              {question.answer_type === "multiple_choice"
                                ? (
                                    <CorrectChoiceSelector
                                      examPaperId={examPaper.id}
                                      question={question}
                                    />
                                  )
                                : question.correct_text_answer && (
                                    <div className="py-1.5 px-2 rounded-md border border-green-200 bg-green-50">
                                      <div className="flex items-start">
                                        <div className="mr-2 font-medium">
                                          Answer:
                                        </div>
                                        <div className="quill-content">
                                          {question.correct_text_answer}
                                        </div>
                                        <Badge color="lime" className="ml-2">
                                          Correct
                                        </Badge>
                                      </div>
                                    </div>
                                  )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Text>No sections available.</Text>
            </div>
          )}
        </div>
      </Fieldset>
    </div>
  );
};

export default ExamPaperDetails;
